import * as React from 'react';

import {
	Col,
	Grid,
	Row,
	Box,
	Sidemenu,
	Skeleton,
	SkeletonText,
	IconButton,
	Label,
	IBoxComponentProps,
	SidemenuItem,
} from '@moosend/mooskin';
import { WizardTitle } from './StepTitle';

// Entities models
import { ILPWizardRoutes, IFormsWizardRoutes } from '../../../../entities/src/model';
import { Link } from 'react-router-dom';
import { SidemenuItemStep } from './SidemenuSteps';

export type stepTypes = ILPWizardRoutes | IFormsWizardRoutes;
export type IWizardWrapperItems = {
	[key: string]: {
		title: string;
		route: string;
		routeChecker?: string | string[];
		hide?: boolean;
		disabled?: boolean;
	};
};

export interface IWizardWrapperProps {
	title?: string | JSX.Element;
	items: IWizardWrapperItems;
	isLoading?: boolean;
	goToSettingsStep: (step: ILPWizardRoutes | IFormsWizardRoutes) => void;
	exitRoute?: string;
	submenuItems?: string[];
	sidemenuProps?: IBoxComponentProps;
	wrapperProps?: IBoxComponentProps;
	onClickExit?: () => void;
	withSteps?: boolean;
}

export const WizardWrapper: React.FC<IWizardWrapperProps> = (props) => {
	const goToSettingsStep = (step: ILPWizardRoutes) => {
		props.goToSettingsStep && props.goToSettingsStep(step);
	};

	const splitPathname = location.pathname.split('/');

	const getItems = () => {
		const sidebarItems = [];
		for (const key in props.items) {
			if (props.items.hasOwnProperty(key) && !props.items[key].hide) {
				const sidebarItem = (
					<SidemenuItem
						cursor={props.items[key].disabled ? 'not-allowed' : 'pointer'}
						key={props.items[key].title}
						value={props.items[key].route}
						active={
							splitPathname.find((item) => {
								return props.items[key].routeChecker
									? Array.isArray(props.items[key].routeChecker)
										? props.items[key].routeChecker?.includes(item)
										: item === props.items[key].routeChecker
									: item === props.items[key].route;
							})
								? true
								: false
						}
						onClick={() => goToSettingsStep(props.items[key].route as ILPWizardRoutes)}
					>
						{props.items[key].title}
					</SidemenuItem>
				);
				sidebarItems.push(sidebarItem);
			}
		}
		return sidebarItems;
	};

	const getItemsAsSteps = () => {
		const sidebarItems = [];
		let index = 0;
		for (const key in props.items) {
			if (props.items.hasOwnProperty(key) && !props.items[key].hide) {
				const sidebarItem = (
					<SidemenuItemStep
						index={index}
						styles={{ cursor: props.items[key].disabled ? 'not-allowed' : 'pointer' }}
						key={props.items[key].title}
						value={props.items[key].route}
						active={
							splitPathname.find((item) => {
								return props.items[key].routeChecker
									? Array.isArray(props.items[key].routeChecker)
										? props.items[key].routeChecker?.includes(item)
										: item === props.items[key].routeChecker
									: item === props.items[key].route;
							})
								? true
								: false
						}
						stepsInOrder={Object.keys(props.items).filter((key) => !props.items[key].hide)}
						onClick={() => goToSettingsStep(props.items[key].route as ILPWizardRoutes)}
					>
						{props.items[key].title}
					</SidemenuItemStep>
				);
				sidebarItems.push(sidebarItem);
				index++;
			}
		}
		return sidebarItems;
	};

	const renderWizardLoader = () => {
		return (
			<Grid>
				<Row>
					<WizardLoaderLeft />
					<WizardLoaderRight />
				</Row>
			</Grid>
		);
	};

	const customTitle = React.Children.map(props.children, (child) => {
		if (React.isValidElement(child) && child.type === CustomWizardTitle) {
			return React.cloneElement(child);
		}
		return null;
	})?.find((item) => item !== null);

	const renderChildren = () => {
		return React.Children.map(props.children, (child) => {
			if (React.isValidElement(child) && child.type === CustomWizardTitle) {
				return null;
			}
			return child;
		});
	};

	const hasItems = () => props.items && Object.keys(props.items).length;

	return (
		<Box position="relative" pb={50}>
			{props.submenuItems && props.exitRoute && (
				<Submenu onClickExit={props.onClickExit} exitRoute={props.exitRoute} items={props.submenuItems} />
			)}
			<Box
				maxW={1500}
				mx="auto"
				py={props.submenuItems && props.exitRoute ? '100px' : '50px'}
				d="flex"
				direction="column"
				position="relative"
				{...props.wrapperProps}
			>
				{props.title ? (
					<Box noRender={['sm', 'xs']} d="flex">
						{<WizardTitle>{props.title}</WizardTitle>}
					</Box>
				) : customTitle ? (
					<Box noRender={['sm', 'xs']} d="flex">
						{customTitle}
					</Box>
				) : null}
				<Grid>
					<Row>
						{hasItems() ? (
							<Col px={30} lg={2} md={3} noRender={['sm', 'xs']}>
								<Sidemenu mb={50} h="fit-content" {...props.sidemenuProps}>
									{props.withSteps ? getItemsAsSteps() : getItems()}
								</Sidemenu>
							</Col>
						) : null}
						<Col lg={!hasItems() ? 12 : 10} md={!hasItems() ? 12 : 9}>
							{props.isLoading ? renderWizardLoader() : renderChildren()}
						</Col>
					</Row>
				</Grid>
			</Box>
		</Box>
	);
};

export const CustomWizardTitle: React.FC<IBoxComponentProps> = (props) => {
	return <Box w="100%" {...props} />;
};

export interface ISubmenuComponentProps {
	exitRoute: string;
	items: string[];
	onClickExit?: () => void;
}

export const Submenu: React.FC<ISubmenuComponentProps> = (props) => {
	return (
		<Box
			noRender={['sm', 'xs']}
			bgColor="backgroundColors.submenu"
			boxShadow="sm"
			zIndex={2}
			h={50}
			d="flex"
			align="center"
			w="100%"
			position="fixed"
			top="90"
		>
			<Link style={{ margin: '0 25px' }} to={props.exitRoute} onClick={props.onClickExit}>
				<Label fontColor="fontColors.inverseText" fontSize={14} fontWeight={500} align="center">
					<IconButton fontColor="fontColors.inverseText" fontSize={14} children="arrow_back" mr={5} />
					Exit
				</Label>
			</Link>
			{props.items.map((item, i) => {
				const isNotLast = i < props.items.length - 1;
				return (
					<React.Fragment key={i}>
						<Label fontColor="fontColors.inverseText" opacity={isNotLast ? 1 : 0.5} fontSize={14} fontWeight={500}>
							{item}
						</Label>
						{isNotLast && (
							<Label fontColor="fontColors.inverseText" fontSize={14} fontWeight={500} mx={5}>
								/
							</Label>
						)}
					</React.Fragment>
				);
			})}
		</Box>
	);
};

export const WizardLoaderLeft = () => {
	return (
		<Col px={30} pb={50} lg={6} md={12} sm={12} xs={12}>
			<Skeleton w="30%" h={20} />
			<Skeleton mt={25} w="20%" h={15} />
			<Box mt={40}>
				<SkeletonText />
			</Box>
			<Skeleton mt={50} w="20%" h={15} />
			<Skeleton mt={15} w="100%" h={35} />
			<Skeleton mt={15} w="100%" h={10} />
			<Skeleton mt={25} w="20%" h={15} />
			<Skeleton mt={15} w="100%" h={35} />
			<Skeleton mt={15} w="100%" h={10} />
			<Box d="flex">
				<Skeleton mt={25} w="20%" h={35} />
				<Box mt={25} w="80%" pl={20}>
					<SkeletonText lines={2} />
				</Box>
			</Box>
		</Col>
	);
};

export const WizardLoaderRight = () => {
	return (
		<Col px={30} lg={6} md={12} sm={12} xs={12}>
			<Skeleton w="30%" h={20} />
			<Skeleton mt={20} w="20%" h={15} />
			<Box d="flex" justify="space-between" align="center">
				<Skeleton mt={25} w="50%" h={15} />
				<Skeleton mt={25} w="15%" h={25} />
			</Box>
			<Skeleton mt={40} w="100%" h={300} />
			<Skeleton mt={40} w="20%" mx="auto" h={40} />
		</Col>
	);
};

export default WizardWrapper;
